import React, { createContext, useState, useContext, useEffect } from 'react';
import { keycloakConfig } from './KeycloakConfig';

const AuthContext = createContext(null);

// Function to parse JWT token
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    console.error('Error parsing JWT token:', e);
    return null;
  }
};

// Function to check if token is expired
const isTokenExpired = (token) => {
  try {
    const decodedToken = parseJwt(token);
    if (!decodedToken || !decodedToken.exp) return true;
    
    // Token expiration time is in seconds, current time needs to be divided by 1000
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (e) {
    console.error('Error checking token expiration:', e);
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken);
      setUser(parseJwt(storedToken));
    } else if (storedToken) {
      // Token exists but is expired
      console.log('Stored token is expired, removing from storage');
      localStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      console.log(`Attempting login to ${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`);
      console.log(`Using client ID: ${keycloakConfig.clientId}`);
      
      const response = await fetch(`${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: keycloakConfig.clientId,
          username,
          password,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Login failed. Server response:', errorText);
        throw new Error(`Login failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Login successful, token received');
      
      // Store token and user info
      setToken(data.access_token);
      const userInfo = parseJwt(data.access_token);
      setUser(userInfo);
      
      localStorage.setItem('token', data.access_token);
      console.log('User info extracted from token:', userInfo);
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    console.log('Logging out...');
    
    // Perform Keycloak logout
    const logoutUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/logout?client_id=${keycloakConfig.clientId}&post_logout_redirect_uri=${encodeURIComponent(window.location.origin + '/login')}`;

    // Clear local storage and state
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    
    console.log('Redirecting to Keycloak logout endpoint');
    window.location.href = logoutUrl;
  };

  // Function to check if user is authenticated
  const isAuthenticated = () => {
    return !!token && !isTokenExpired(token);
  };

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      login, 
      logout, 
      loading, 
      isAuthenticated 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};